
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { MissionData, UserMissionProgress, RewardShipment } from '@/types/missions';
import { debugLogger } from '@/utils/debugLogger';
import { showSuccessToast, showErrorToast } from '@/utils/toastUtils';
import { useMissionValidation } from './useMissionValidation';

export const useMissions = () => {
  const { user } = useAuth();
  const [missionData, setMissionData] = useState<MissionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [rewardShipment, setRewardShipment] = useState<RewardShipment | null>(null);
  const { validateAllMissions, validating } = useMissionValidation();

  const fetchMissionData = useCallback(async () => {
    if (!user?.id) {
      setMissionData(null);
      setRewardShipment(null);
      setLoading(false);
      return;
    }

    try {
      // Buscar missão ativa (mais recente)
      const { data: missions, error: missionError } = await supabase
        .from('missions')
        .select('*')
        .eq('is_active', true)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (missionError) throw missionError;

      // Verificar se existe missão ativa
      if (!missions) {
        debugLogger.warn('[useMissions] No active mission found');
        setMissionData(null);
        setRewardShipment(null);
        setLoading(false);
        return;
      }

      // Buscar etapas da missão
      const { data: stepsData, error: stepsError } = await supabase
        .from('mission_steps')
        .select('*')
        .eq('mission_id', missions.id)
        .order('day_number');

      if (stepsError) throw stepsError;

      // Convert steps data to proper types
      const steps = stepsData?.map(step => ({
        ...step,
        validation_criteria: step.validation_criteria as Record<string, any>
      })) || [];

      // Buscar missão do usuário
      const { data: userMissionData, error: userMissionError } = await supabase
        .from('user_missions')
        .select('*')
        .eq('user_id', user.id)
        .eq('mission_id', missions.id)
        .maybeSingle();

      if (userMissionError) throw userMissionError;

      // Convert user mission data to proper type
      const userMission = userMissionData ? {
        ...userMissionData,
        status: userMissionData.status as 'ongoing' | 'completed' | 'expired'
      } : undefined;

      // Buscar progresso se existe missão do usuário
      let progress: UserMissionProgress[] = [];
      if (userMission) {
        const { data: progressData, error: progressError } = await supabase
          .from('user_mission_progress')
          .select('*')
          .eq('user_mission_id', userMission.id)
          .order('day_number');

        if (progressError) throw progressError;
        
        // Convert progress data to proper types
        progress = progressData?.map(p => ({
          ...p,
          status: p.status as 'pending' | 'available' | 'completed'
        })) || [];
      }

      // Buscar reward shipment (sempre, independente do status da missão)
      let shipment = null;
      const { data: shipmentData, error: shipmentError } = await supabase
        .from('reward_shipments')
        .select('*')
        .eq('user_id', user.id)
        .eq('mission_id', missions.id)
        .order('submitted_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (shipmentError) {
        debugLogger.error('[useMissions] Error fetching reward shipment:', shipmentError);
      } else if (shipmentData) {
        debugLogger.debug('[useMissions] Found existing reward shipment:', { 
          id: shipmentData.id, 
          submitted_at: shipmentData.submitted_at 
        });
      }
      
      shipment = shipmentData;

      setMissionData({
        mission: missions,
        steps,
        userMission,
        progress
      });
      
      setRewardShipment(shipment);

    } catch (error) {
      debugLogger.error('[useMissions] Error fetching mission data:', error);
      showErrorToast({
        title: "Erro ao carregar missões",
        description: "Não foi possível carregar suas missões."
      });
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const refreshMissionData = useCallback(async () => {
    debugLogger.debug('[useMissions] Refreshing mission data after validation');
    await fetchMissionData();
  }, [fetchMissionData]);

  const submitRewardInfo = useCallback(async (rewardData: Omit<RewardShipment, 'id' | 'user_id' | 'mission_id' | 'sent' | 'submitted_at' | 'created_at'>) => {
    if (!user?.id || !missionData?.mission) return false;

    try {
      const { error } = await supabase
        .from('reward_shipments')
        .insert({
          user_id: user.id,
          mission_id: missionData.mission.id,
          ...rewardData
        });

      // Handle unique violation (user already submitted)
      if (error?.code === '23505') {
        debugLogger.debug('[useMissions] Reward shipment already exists, refreshing data');
        await fetchMissionData();
        showSuccessToast({
          title: "Dados já enviados",
          description: "Você já enviou seus dados anteriormente. Atualizamos a página."
        });
        return true;
      }

      if (error) throw error;

      showSuccessToast({
        title: "Parabéns!",
        description: "Seus dados foram enviados com sucesso. Em breve entraremos em contato!"
      });

      // Recarregar dados para atualizar rewardShipment
      await fetchMissionData();

      return true;
    } catch (error) {
      debugLogger.error('[useMissions] Error submitting reward info:', error);
      showErrorToast({
        title: "Erro ao enviar dados",
        description: "Não foi possível enviar seus dados para entrega."
      });
      return false;
    }
  }, [user?.id, missionData?.mission, fetchMissionData]);

  useEffect(() => {
    const loadData = async () => {
      await fetchMissionData();
      // Validate all missions on mount
      if (user?.id) {
        debugLogger.debug('[useMissions] Auto-validating missions on mount');
        const result = await validateAllMissions();
        if (result) {
          debugLogger.debug('[useMissions] Validation complete, refreshing data');
          await fetchMissionData();
        }
      }
    };
    loadData();
  }, [user?.id]);

  return {
    missionData,
    loading,
    validating,
    validateAllMissions,
    refreshMissionData,
    submitRewardInfo,
    refetch: fetchMissionData,
    rewardShipment
  };
};
